import React, { useState, useEffect } from 'react';
import {
    STATE_PRESETS,
    FAMILY_SITUATIONS,
    USAGE_PROFILES,
    INSURANCE_PLAN_PRESETS,
    compareCurrentVsSafecare,
    formatCurrency,
    BASELINE_MODES
} from '../utils/calculatorLogic';
import SEO from '../components/SEO';
import PageHero from '../components/layout/PageHero';
// import ShockTherapySuggestions from '../components/content/ShockTherapySuggestions';
import { motion } from 'framer-motion';
import { parseMarkdownItalics } from '../components/SafecareBrand';
import '../styles/pages/calculator.css';

const Calculator = () => {
    const [inputs, setInputs] = useState({
        stateCode: 'TX',
        familySituation: 'SINGLE_WORKER',
        householdMembers: FAMILY_SITUATIONS.SINGLE_WORKER.members,
        insurancePlan: null, // null = use state average
        baselineMode: BASELINE_MODES.STATE_AVERAGE
    });

    const [results, setResults] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showPerPersonBreakdown, setShowPerPersonBreakdown] = useState(false);
    const [incomeMode, setIncomeMode] = useState('simple'); // 'simple' or 'complex'

    // Calculate results whenever inputs change
    useEffect(() => {
        try {
            const comparison = compareCurrentVsSafecare(inputs);
            setResults(comparison);
        } catch (error) {
            console.error('Calculation error:', error);
        }
    }, [inputs]);

    const handleStateChange = (stateCode) => {
        setInputs(prev => ({ ...prev, stateCode }));
    };

    const handleSituationChange = (situationKey) => {
        setInputs(prev => ({
            ...prev,
            familySituation: situationKey,
            householdMembers: FAMILY_SITUATIONS[situationKey].members
        }));
    };

    const handleMemberUpdate = (index, field, value) => {
        setInputs(prev => ({
            ...prev,
            householdMembers: prev.householdMembers.map((member, i) =>
                i === index ? { ...member, [field]: value } : member
            )
        }));
    };

    const addHouseholdMember = () => {
        setInputs(prev => ({
            ...prev,
            householdMembers: [
                ...prev.householdMembers,
                {
                    role: `person${prev.householdMembers.length + 1}`,
                    age: 30,
                    income: 0,
                    wages: 0,
                    passiveIncome: 0,
                    retirementIncome: 0,
                    isSelfEmployed: false,
                    usageProfile: 'MEDIUM'
                }
            ]
        }));
    };

    const removeMember = (index) => {
        if (inputs.householdMembers.length <= 1) return; // Keep at least one member
        setInputs(prev => ({
            ...prev,
            householdMembers: prev.householdMembers.filter((_, i) => i !== index)
        }));
    };

    // Reset plan if it becomes invalid for the current household size
    useEffect(() => {
        if (!inputs.insurancePlan) return;
        const plan = INSURANCE_PLAN_PRESETS[inputs.insurancePlan];
        if (!plan) return;

        const isFamily = inputs.householdMembers.length > 1;
        if ((isFamily && plan.type === 'individual') || (!isFamily && plan.type === 'family')) {
            setInputs(prev => ({ ...prev, insurancePlan: null })); // Reset to State Average
        }
    }, [inputs.householdMembers.length, inputs.insurancePlan]);

    const isFamily = inputs.householdMembers.length > 1;

    // Filter plans based on household size
    const availablePlans = Object.entries(INSURANCE_PLAN_PRESETS).filter(([key, plan]) => {
        // Special case: Medicaid/Medicare are technically 'individual' type in our data
        // but might be relevant for some checks. For simplicity, we stick to strict type matching
        // to avoid the "Family of 4 on Individual Plan" price error.
        if (isFamily) return plan.type === 'family';
        return plan.type === 'individual';
    });

    if (!results) return <div className="calculator-loading">Loading calculator...</div>;

    const employeeSaves = results.employeeSavings > 0;
    const employerSaves = results.employerSavings > 0;

    return (
        <>
            <SEO
                title="SAFEC+ Savings Calculator"
                description="Calculate how much you and your employer would save under SAFEC+. Compare current healthcare costs with SAFEC+'s per-person contribution model for all 50 states."
                image="/image/safecare_plus_workers.png"
            />
            <div className="visual-page visual-page-calculator">
                {/* Animated Background Blobs */}
                <div className="aura-container">
                    <motion.div
                        className="aura-blob aura-blue anim-blob-float blob-1"
                        animate={{ x: [0, 50, -50, 0], y: [0, -50, 50, 0] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    />
                    <motion.div
                        className="aura-blob aura-red anim-blob-float blob-2"
                        animate={{ x: [0, -30, 30, 0], y: [0, 60, -60, 0] }}
                        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                    />
                </div>

                <PageHero
                    title="Savings Calculator"
                    subtitle="See how much your household would save under SAFEC+. Compare current healthcare costs with SAFEC+'s per-person contribution model for all 50 states."
                    image="/image/safecare_plus_workers.png"
                    badge="SAFEC+"
                    className="calculator-hero"
                />

                <div className="container" style={{ marginTop: '-4rem', position: 'relative', zIndex: 20 }}>
                    {/* Input Section */}
                    <div className="calculator-inputs">
                        <p className="calculator-note" style={{ marginBottom: '2rem', textAlign: 'center' }}>
                            <strong>Note:</strong> {parseMarkdownItalics("SAFEC+")} contributions are calculated per person, not per household (§203)
                        </p>
                        <div className="input-row">
                            <div className="input-group">
                                <label htmlFor="state-select">Your State</label>
                                <select
                                    id="state-select"
                                    value={inputs.stateCode}
                                    onChange={(e) => handleStateChange(e.target.value)}
                                    className="calculator-select"
                                >
                                    {Object.entries(STATE_PRESETS).map(([code, data]) => (
                                        <option key={code} value={code}>
                                            {data.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="input-group">
                                <label htmlFor="situation-select">Family Situation</label>
                                <select
                                    id="situation-select"
                                    value={inputs.familySituation}
                                    onChange={(e) => handleSituationChange(e.target.value)}
                                    className="calculator-select"
                                >
                                    {Object.entries(FAMILY_SITUATIONS).map(([key, data]) => (
                                        <option key={key} value={key}>
                                            {data.label}
                                        </option>
                                    ))}
                                </select>
                                <span className="input-hint">
                                    {FAMILY_SITUATIONS[inputs.familySituation].description}
                                </span>
                            </div>
                        </div>

                        <div className="input-row">
                            <div className="input-group">
                                <label htmlFor="plan-select">Current Insurance Plan</label>
                                <select
                                    id="plan-select"
                                    value={inputs.insurancePlan || ''}
                                    onChange={(e) => setInputs(prev => ({
                                        ...prev,
                                        insurancePlan: e.target.value || null,
                                        baselineMode: e.target.value ? BASELINE_MODES.EMPLOYER_PLAN : BASELINE_MODES.STATE_AVERAGE
                                    }))}
                                    className="calculator-select"
                                >
                                    <option value="">Use State Average (Recommended)</option>
                                    <optgroup label="Select Comparison Baseline">
                                        <option value={BASELINE_MODES.STATE_AVERAGE}>State Average Costs</option>
                                        <option value={BASELINE_MODES.RETIREE_MEDICARE_LIKE}>Retiree (Medicare-style)</option>
                                        <option value={BASELINE_MODES.UNINSURED_OOP}>Uninsured (OOP Risk)</option>
                                    </optgroup>
                                    <optgroup label="Specific Employer Plans">
                                        {isFamily ? (
                                            availablePlans
                                                .filter(([key]) => key.includes('HDHP') || key.includes('PPO') || key.includes('HMO') || key.includes('EPO') || key.includes('PREMIUM'))
                                                .map(([key, plan]) => (
                                                    <option key={key} value={key}>{plan.label}</option>
                                                ))
                                        ) : (
                                            availablePlans
                                                .filter(([key]) => key.includes('HDHP') || key.includes('PPO') || key.includes('HMO') || key.includes('EPO') || key.includes('PREMIUM'))
                                                .map(([key, plan]) => (
                                                    <option key={key} value={key}>{plan.label}</option>
                                                ))
                                        )}
                                    </optgroup>
                                    {!isFamily && (
                                        <>
                                            <optgroup label="ACA Marketplace">
                                                {availablePlans
                                                    .filter(([key]) => key.includes('ACA') || key.includes('CATASTROPHIC'))
                                                    .map(([key, plan]) => (
                                                        <option key={key} value={key}>{plan.label}</option>
                                                    ))}
                                            </optgroup>
                                            <optgroup label="Government Programs">
                                                {availablePlans
                                                    .filter(([key]) => key.includes('MEDICARE') || key.includes('MEDICAID'))
                                                    .map(([key, plan]) => (
                                                        <option key={key} value={key}>{plan.label}</option>
                                                    ))}
                                            </optgroup>
                                        </>
                                    )}
                                </select>
                                {inputs.insurancePlan && INSURANCE_PLAN_PRESETS[inputs.insurancePlan] && (
                                    <span className="input-hint">
                                        {INSURANCE_PLAN_PRESETS[inputs.insurancePlan].description}
                                    </span>
                                )}
                                {!inputs.insurancePlan && inputs.baselineMode !== BASELINE_MODES.STATE_AVERAGE && (
                                    <span className="input-hint">
                                        Mode: {inputs.baselineMode.replace(/_/g, ' ').toUpperCase()}
                                    </span>
                                )}
                            </div>

                            <div className="input-group">
                                <label>Income Complexity</label>
                                <div className="toggle-buttons">
                                    <button
                                        className={`toggle-btn ${incomeMode === 'simple' ? 'active' : ''}`}
                                        onClick={() => setIncomeMode('simple')}
                                    >
                                        Simple (W-2 Only)
                                    </button>
                                    <button
                                        className={`toggle-btn ${incomeMode === 'complex' ? 'active' : ''}`}
                                        onClick={() => setIncomeMode('complex')}
                                    >
                                        Complex (All Income)
                                    </button>
                                </div>
                                <span className="input-hint">
                                    {incomeMode === 'simple' ? 'Wages only' : 'Wages + passive/investment income'}
                                </span>
                            </div>
                        </div>

                        {/* Household Members */}
                        <div className="household-members-section">
                            <div className="section-header">
                                <h3>Household Members ({inputs.householdMembers.length})</h3>
                                <button className="add-member-btn" onClick={addHouseholdMember}>
                                    + Add Member
                                </button>
                            </div>

                            {inputs.householdMembers.map((member, index) => (
                                <div key={index} className="member-card">
                                    <div className="member-header">
                                        <span className="member-label">
                                            {member.age < 18 ? '👶' : '👤'} Member {index + 1}
                                            {member.age < 18 && ' (Child)'}
                                        </span>
                                        {inputs.householdMembers.length > 1 && (
                                            <button
                                                className="remove-member-btn"
                                                onClick={() => removeMember(index)}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>

                                    <div className="member-inputs">
                                        <div className="input-group-small">
                                            <label>Age</label>
                                            <input
                                                type="number"
                                                value={member.age}
                                                onChange={(e) => handleMemberUpdate(index, 'age', parseInt(e.target.value) || 0)}
                                                className="calculator-input"
                                                min="0"
                                                max="120"
                                            />
                                        </div>

                                        <div className="input-group-small">
                                            <label>Annual Income</label>
                                            <div className="input-with-prefix-small">
                                                <span className="input-prefix">$</span>
                                                <input
                                                    type="number"
                                                    value={member.income}
                                                    onChange={(e) => {
                                                        const income = parseInt(e.target.value) || 0;
                                                        handleMemberUpdate(index, 'income', income);
                                                        if (incomeMode === 'simple') {
                                                            handleMemberUpdate(index, 'wages', income); // Auto-sync wages in simple mode
                                                        }
                                                    }}
                                                    className="calculator-input"
                                                    min="0"
                                                    step="1000"
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group-small">
                                            <label>Healthcare Use</label>
                                            <select
                                                value={member.usageProfile}
                                                onChange={(e) => handleMemberUpdate(index, 'usageProfile', e.target.value)}
                                                className="calculator-select"
                                            >
                                                {Object.entries(USAGE_PROFILES).map(([key, profile]) => (
                                                    <option key={key} value={key}>
                                                        {profile.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="input-group-small checkbox-group">
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={member.isSelfEmployed}
                                                    onChange={(e) => handleMemberUpdate(index, 'isSelfEmployed', e.target.checked)}
                                                />
                                                Self-Employed
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            className="advanced-toggle"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                        >
                            {showAdvanced ? '▼' : '▶'} Advanced Options
                        </button>

                        {showAdvanced && (
                            <div className="advanced-options">
                                <p className="advanced-note">
                                    Customize income components for precise tax calculations (§203)
                                </p>
                                {inputs.householdMembers.map((member, index) => (
                                    <div key={index} className="advanced-member-row">
                                        <h4>Member {index + 1}</h4>
                                        <div className="input-row">
                                            <div className="input-group">
                                                <label>Annual Wages (W-2)</label>
                                                <div className="input-with-prefix">
                                                    <span className="input-prefix">$</span>
                                                    <input
                                                        type="number"
                                                        value={member.wages}
                                                        onChange={(e) => handleMemberUpdate(index, 'wages', parseInt(e.target.value) || 0)}
                                                        className="calculator-input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="input-group">
                                                <label>Retirement Income (§203)</label>
                                                <div className="input-with-prefix">
                                                    <span className="input-prefix">$</span>
                                                    <input
                                                        type="number"
                                                        value={member.retirementIncome}
                                                        onChange={(e) => handleMemberUpdate(index, 'retirementIncome', parseInt(e.target.value) || 0)}
                                                        className="calculator-input"
                                                    />
                                                </div>
                                                <span className="input-hint">First $100k is exempt</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Results Display */}
                    <div className="calculator-results">
                        {/* Savings Summary */}
                        <div className={`savings-banner ${results.householdFinancialBenefit > 0 ? 'positive' : 'negative'}`}>
                            <div className="savings-icon">🛡️</div>
                            <div className="savings-content">
                                <div className="savings-label">Household Financial Benefit</div>
                                <div className="savings-amount">
                                    {results.householdFinancialBenefit > 0 ? '+' : ''}{formatCurrency(results.householdFinancialBenefit)}
                                </div>
                                <div className="savings-monthly">
                                    ({formatCurrency(results.householdFinancialBenefit / 12)}/month)
                                </div>
                            </div>
                        </div>

                        {results.safecareEfficiencyDividend > 0 && (
                            <div className="dividend-banner">
                                <span className="dividend-badge">§202(f) COMPENSATION PASS-THROUGH</span>
                                <div className="dividend-text">
                                    <strong>+{formatCurrency(results.safecareEfficiencyDividend)}/yr</strong> Estimated Wage Pass-through
                                    <span className="info-icon" title="Employers must pay back healthcare savings to eligible workers as wages (§202(f))."> ⓘ</span>
                                </div>
                                <p className="dividend-small">
                                    Allocated to {results.eligibleWorkerCount} eligible worker{results.eligibleWorkerCount !== 1 ? 's' : ''} in your household.
                                </p>
                            </div>
                        )}

                        {/* Side-by-Side Comparison */}
                        <div className="comparison-cards">
                            {/* Employee Card */}
                            <div className="comparison-section">
                                <h3>For Your Household</h3>
                                <div className="cost-cards">
                                    <div className="cost-card current">
                                        <div className="card-header">
                                            <span className="card-title">Current System</span>
                                        </div>
                                        <div className="card-body">
                                            <div className="cost-line">
                                                <span>Premium (your share)</span>
                                                <span>{formatCurrency(results.currentEmployeePremium)}</span>
                                            </div>
                                            <div className="cost-line">
                                                <span>Out-of-Pocket</span>
                                                <span>{formatCurrency(results.currentEmployeeOOP)}</span>
                                            </div>
                                            <div className="cost-total">
                                                <span>Total Annual Cost</span>
                                                <span>{formatCurrency(results.currentEmployeeTotal)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cost-card safecare">
                                        <div className="card-header">
                                            <span className="card-title">{parseMarkdownItalics("SAFEC+")}</span>
                                        </div>
                                        <div className="card-body">
                                            <div className="cost-line">
                                                <span>Contributions (all members)</span>
                                                <span>{formatCurrency(results.safecareContribution)}</span>
                                            </div>
                                            <div className="cost-line">
                                                <span>Copays (all members)</span>
                                                <span>{formatCurrency(results.safecareCopays)}</span>
                                            </div>
                                            <div className="cost-total">
                                                <span>Total Annual Cost</span>
                                                <span>{formatCurrency(results.safecareEmployeeTotal)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={`savings-indicator ${results.householdFinancialBenefit > 0 ? 'positive' : 'negative'}`}>
                                    <span className="savings-label">
                                        Total Financial Benefit:
                                        <span className="info-icon" title="Combines your direct healthcare savings and the estimated employer wage pass-through."> ⓘ</span>
                                    </span>
                                    <span className="savings-value">
                                        {results.householdFinancialBenefit > 0 ? '+' : ''}{formatCurrency(results.householdFinancialBenefit)}
                                    </span>
                                </div>
                                <p className="formula-hint">Direct Savings + Wage Pass-through</p>
                            </div>

                            {/* Employer Card */}
                            <div className="comparison-section">
                                <h3>For Employers (Combined)</h3>
                                <div className="cost-cards">
                                    <div className="cost-card current">
                                        <div className="card-header">
                                            <span className="card-title">Current System</span>
                                        </div>
                                        <div className="card-body">
                                            <div className="cost-line">
                                                <span>Premium (employer share)</span>
                                                <span>{formatCurrency(results.currentEmployerPremium)}</span>
                                            </div>
                                            <div className="cost-total">
                                                <span>Total Annual Cost</span>
                                                <span>{formatCurrency(results.currentEmployerPremium)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cost-card safecare">
                                        <div className="card-header">
                                            <span className="card-title">{parseMarkdownItalics("SAFEC+")}</span>
                                        </div>
                                        <div className="card-body">
                                            <div className="cost-line">
                                                <span>Payroll Contribution (8%)</span>
                                                <span>{formatCurrency(results.safecareEmployerContribution)}</span>
                                            </div>
                                            <div className="cost-total">
                                                <span>Total Annual Cost</span>
                                                <span>{formatCurrency(results.safecareEmployerContribution)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={`savings-indicator ${employerSaves ? 'neutral' : 'negative'}`}>
                                    <span className="savings-label">Net Corporate Cost Δ:</span>
                                    <span className="savings-value">
                                        {formatCurrency(results.employerSavings)}
                                    </span>
                                </div>
                                <p className="formula-hint">Includes redistribution of Dividend (§202)</p>
                            </div>
                        </div>

                        {/* Per-Person Breakdown */}
                        <div className="per-person-section">
                            <button
                                className="breakdown-toggle"
                                onClick={() => setShowPerPersonBreakdown(!showPerPersonBreakdown)}
                            >
                                {showPerPersonBreakdown ? '▼' : '▶'} View Per-Person {parseMarkdownItalics("SAFEC+")} Breakdown
                            </button>

                            {showPerPersonBreakdown && (
                                <div className="per-person-breakdown">
                                    <p className="breakdown-note">
                                        {parseMarkdownItalics("SAFEC+")} contributions are calculated individually for each person (§203)
                                    </p>
                                    {results.memberBreakdown.map(({ person, costs }, index) => (
                                        <div key={index} className="person-breakdown-card">
                                            <div className="person-info">
                                                <span className="person-icon">{person.age < 18 ? '👶' : '👤'}</span>
                                                <div>
                                                    <strong>Member {index + 1}</strong>
                                                    <span className="person-details">
                                                        Age {person.age}, {formatCurrency(person.income)} income
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="person-costs">
                                                <div className="cost-item">
                                                    <span>Contribution:</span>
                                                    <span>{formatCurrency(costs.individualContribution)}</span>
                                                </div>
                                                <div className="cost-item">
                                                    <span>Employer Pay (8%):</span>
                                                    <span>{formatCurrency(costs.employerContribution)}</span>
                                                </div>
                                                <div className="cost-item">
                                                    <span>Copays:</span>
                                                    <span>{formatCurrency(costs.copays)}</span>
                                                </div>
                                                <div className="cost-item total">
                                                    <span>My Net Cost:</span>
                                                    <span>{formatCurrency(costs.totalPersonCost)}</span>
                                                </div>
                                                {costs.efficiencyDividend > 0 && (
                                                    <div className="cost-item dividend-item">
                                                        <span>Est. Wage Pass-through:</span>
                                                        <span className="positive">+{formatCurrency(costs.efficiencyDividend)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Key Benefits */}
                        <div className="benefits-section">
                            <h3>What You Get with {parseMarkdownItalics("SAFEC+")}</h3>
                            <div className="benefits-grid">
                                <div className="benefit-card">
                                    <div className="benefit-icon">✓</div>
                                    <div className="benefit-text">
                                        <strong>No Deductibles</strong>
                                        <p>Start using care immediately</p>
                                    </div>
                                </div>
                                <div className="benefit-card">
                                    <div className="benefit-icon">✓</div>
                                    <div className="benefit-text">
                                        <strong>No Networks</strong>
                                        <p>See any participating doctor</p>
                                    </div>
                                </div>
                                <div className="benefit-card">
                                    <div className="benefit-icon">✓</div>
                                    <div className="benefit-text">
                                        <strong>Per-Person Copay Cap</strong>
                                        <p>Maximum $750/year per person</p>
                                    </div>
                                </div>
                                <div className="benefit-card">
                                    <div className="benefit-icon">✓</div>
                                    <div className="benefit-text">
                                        <strong>Income-Based</strong>
                                        <p>Each person pays based on their income</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Disclaimer */}
                        <div className="calculator-disclaimer">
                            <p>
                                <strong>Note:</strong> This calculator uses average costs based on state and household size.
                                Your actual costs may vary. {parseMarkdownItalics("SAFEC+")} contribution rates are based on the proposed legislation (§203, §202).
                                <strong> Contributions are calculated per person, not per household.</strong>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Contextual Deep Dive suggestions */}
                {/* <ShockTherapySuggestions
                    count={3}
                    title="Continue the Deep Dive: Shock Therapy"
                /> */}
            </div >
        </>
    );
};

export default Calculator;
